<template>
  <div
    v-on-click-outside="onClickOutsideHandler"
    class="relative flex flex-1 flex-col space-y-1"
    :class="[disabled ? 'bg-gray-50' : 'bg-white']"
    data-cy="stip-dropdown"
  >
    <div class="relative" ref="searchInputRef">
      <input
        :value="stipText"
        @input="updateStipText"
        data-cy="file-type-dd"
        class="relative flex h-10 w-full items-center rounded-sm py-1 pr-8 text-left text-sm focus:ring-0"
        :class="[
          dropdownOpen
            ? 'border-2 border-blue-500 pl-8 hover:border-blue-500'
            : 'border border-gray-200',
          disabled ? 'text-gray-400' : 'text-headline',
          { 'border-red-600': hasError },
        ]"
        :disabled="disabled"
        :placeholder="placeholderText"
        @click.stop="dropdownOpen = true"
      />
      <div class="absolute top-2.5 left-0 pl-2">
        <icon-base
          v-if="dropdownOpen"
          class="relative"
          width="19"
          height="18"
          :icon="IconSearch"
        />
      </div>
      <div
        class="absolute inset-y-0 right-0 ml-3 flex cursor-pointer items-center pr-2"
      >
        <icon-base
          v-if="!dropdownOpen"
          height="6"
          width="10"
          :icon="IconDropdownArrow"
          color="#D4D8E2"
          @click.stop="dropdownOpen = true"
        />
        <icon-x v-if="dropdownOpen && !!stipText" @click="stipText = ''" />
      </div>
    </div>
    <field-error-message :value="fieldError" />
    <div
      v-if="dropdownOpen"
      class="absolute z-100 overflow-y-auto rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-hidden"
      :class="[
        positionState.isFlippedVertical ? 'bottom-full' : 'top-full',
        {
          'left-0': !positionState.isFlippedHorizontal,
          'right-0': positionState.isFlippedHorizontal,
        },
        dropdownTeleported ? 'max-w-50-screen' : 'w-full',
      ]"
      :style="dropdownStyle"
      ref="dropdownWrapper"
      data-cy="stip-dropdown-menu"
    >
      <div>
        <div v-for="(group, index) in filteredGroups" :key="group.id">
          <div
            class="border-y px-4 py-2 font-bold text-headline"
            :class="{ 'my-2': index > 0 }"
          >
            {{ group.name }}
          </div>
          <div class="flex flex-col pl-5">
            <div
              v-for="category in getFilteredCategories(group)"
              :key="category.type"
              :data-cy="`category-${category.type}`"
              class="my-2 flex w-max cursor-pointer flex-col"
              @click="selectCategory(category)"
            >
              {{ category.name }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { type CSSProperties, computed, onBeforeUnmount, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import { useField } from "vee-validate";

import { getStipNameByType } from "@/helpers/common";
import { useTeleportV2 } from "@/hooks/elements";
import { useOverflowDetection } from "@/hooks/useOverflowDetection";

import IconDropdownArrow from "@/components/icons/IconDropdownArrow.vue";
import IconSearch from "@/components/icons/IconSearch.vue";
import IconX from "@/components/icons/deals/IconX.vue";
import IconBase from "@/components/ui/IconBase.vue";
import FieldErrorMessage from "@/components/ui/inputs/FieldErrorMessage.vue";

import type { IStip } from "@/models/applications";
import type { DocumentType, StipsCollection } from "@/models/common";

type Props = {
  modelValue?: string;
  model?: IStip;
  placeholder?: string;
  disabled?: boolean;
  name?: string;
  dropdownTeleported?: boolean;
};

const {
  modelValue = "",
  model,
  placeholder = "",
  disabled = false,
  name = "stip-dropdown-category",
  dropdownTeleported = false,
} = defineProps<Props>();

const emit = defineEmits<{
  change: [string | number];
  "update:modelValue": [string];
}>();

const { t } = useI18n();
const store = useStore();

const { errorMessage: fieldError, handleChange } = useField(name, undefined, {
  initialValue: modelValue,
});

const stipText = ref("");
const dropdownOpen = ref(false);
const dropdownWrapper = ref<HTMLDivElement | null>(null);
const searchInputRef = ref<HTMLDivElement | null>(null);

const { positionState, setupEventListeners, updatePosition } =
  useOverflowDetection(dropdownWrapper, searchInputRef, dropdownOpen, {
    checkViewportOverflow: true,
    enableAutoSizing: true,
    offset: { top: 10, bottom: 10 },
  });

const stipCollection = computed<StipsCollection[]>(
  () => store.getters["options/fileTypes"] ?? []
);

const filteredGroups = computed(() => {
  if (!stipText.value) {
    return stipCollection.value;
  }
  const searchTextLower = stipText.value.toLowerCase().trim();
  return stipCollection.value.filter((group) => {
    return (
      group.name.toLowerCase().trim().includes(searchTextLower) ||
      getFilteredCategories(group).length > 0
    );
  });
});

const placeholderText = computed(() => {
  if (dropdownOpen.value) {
    return t("DEALS.DOCUMENTS.SEARCH_DOCUMENT_TYPE");
  }

  if (placeholder) {
    return placeholder;
  }

  return t("STIPS.SELECT_FILE_TYPE");
});

const hasError = computed(() => Boolean(fieldError.value));

const dropdownStyle = computed<CSSProperties>(() => {
  const style: CSSProperties = {
    maxHeight: "50vh",
    overflowY: "auto",
  };

  if (positionState.value.maxHeight) {
    const calculatedMaxHeight = positionState.value.maxHeight;
    const halfScreenHeight = window.innerHeight / 2;
    style.maxHeight = `${Math.min(calculatedMaxHeight, halfScreenHeight)}px`;
  }

  return style;
});

const selectCategory = async (category: DocumentType) => {
  dropdownOpen.value = false;
  handleChange(category.type);
  emit("change", category.type);
  emit("update:modelValue", category.type);
  if (model) {
    await store.dispatch("stips/updateStipFileType", {
      id: model.id,
      file_type: category.type,
    });
  }
  stipText.value = getStipNameByType(modelValue, stipCollection.value);
};

const getFilteredCategories = (group: StipsCollection) => {
  if (!stipText.value) {
    return group.categories;
  }
  const searchTextLower = stipText.value.toLowerCase().trim();
  return group.categories.filter((category: DocumentType) => {
    return (
      category.name.toLowerCase().trim().includes(searchTextLower) ||
      group.name.toLowerCase().trim().includes(searchTextLower)
    );
  });
};

if (dropdownTeleported) {
  useTeleportV2({
    target: dropdownWrapper,
    to: "#menus",
    offset: { top: 45 },
    checkForOverflow: true,
  });
}

const onClickOutsideHandler = [
  () => {
    dropdownOpen.value = false;
    stipText.value = getStipNameByType(modelValue, stipCollection.value);
  },
  { ignore: [searchInputRef] },
];

const updateStipText = (e: Event) => {
  const target = e.target as HTMLInputElement;
  stipText.value = target.value;
};

const cleanup = setupEventListeners();

watch(
  () => modelValue,
  (newValue) => {
    stipText.value = getStipNameByType(newValue, stipCollection.value);
  },
  { immediate: true }
);

watch(dropdownOpen, (isOpen) => {
  if (isOpen) {
    updatePosition();
  }
});

onBeforeUnmount(() => {
  cleanup();
});
</script>
